import { useContext, useEffect, useState } from "react";
import EnterpriseService from "../../common/services/enterprise-service";
import { Button, Card, CardBody, CardTitle } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import config from "../../config";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import CustomBadge from "../../components/ui/CustomBadge";
import truckyService from "../../common/services/trucky-service";
import { EnterpriseContext } from "../../components/enterprise/EnterpriseContext";
import NitroAdContainer from "../../ads/NitroAdContainer";
import LazyImage from "../components/common/LazyImage";

const Homepage = ({ container }) => {
  const { globalContext } = useContext(EnterpriseContext);

  const es = new EnterpriseService();
  const [stats, setStats] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  const navigate = useNavigate();
  const loginUrl = `${
    config.ENTERPRISE_URL
  }/authentication/login/steam?returnUrl=auth${encodeURIComponent(
    `?returnUrl=${window.location.pathname}`
  )}`;

  useEffect(() => {
    document.title = "Trucky - The Virtual Trucker Companion App";
  }, []);

  useEffect(() => {
    es.getOverallStats().then(setStats);
    es.getGlobalCompanyStats().then(setCompanyStats);
  }, []);

  const getCompanyLogo = (company) => {
    return <LazyImage src={company.avatar_url} className="mb-3 rounded" style={{ maxHeight: 100, alignSelf: 'center' }} placeholder="/assets/company-placeholder.png" />
  };

  const getCompanyStyleBadge = (company) => {
    if (company.company_type == "realistic")
      return (
        <CustomBadge size="small" color="primary">
          {container.localeManager.strings.realisticEconomy}{" "}
          <FontAwesomeIcon className="ms-1" icon="users"></FontAwesomeIcon>
          {company.members_count}
        </CustomBadge>
      );

    if (company.company_type == "miles")
      return (
        <CustomBadge size="small" color="primary">
          {container.localeManager.strings.enterpriseMiles}{" "}
          <FontAwesomeIcon className="ms-1" icon="users"></FontAwesomeIcon>
          {company.members_count}
        </CustomBadge>
      );

    if (company.company_type == "both")
      return (
        <CustomBadge size="small" color="primary">
          {container.localeManager.strings.ecoAndMiles}{" "}
          <FontAwesomeIcon className="ms-1" icon="users"></FontAwesomeIcon>
          {company.members_count}
        </CustomBadge>
      );
  };

  const statCards = stats != null && [
    {
      key: "drivers",
      label: container.localeManager.strings.drivers,
      value: container.localeManager.numeral(stats.total_members).format(),
      icon: "users",
      color: "primary",
    },
    {
      key: "companies",
      label: container.localeManager.strings.companies,
      value: container.localeManager.numeral(stats.companies).format(),
      icon: "building",
      color: "info",
    },
    {
      key: "deliveries",
      label: container.localeManager.strings.deliveries,
      value: container.localeManager.numeral(stats.total_jobs).format(),
      icon: "shipping-fast",
      color: "success",
    },
    {
      key: "distance",
      label: container.localeManager.strings.distance,
      value: `${container.localeManager
        .numeral(stats.overall.distance_driven_on_job)
        .format()} km`,
      icon: "route",
      color: "warning",
    },
  ];

  return (
    <div className="homepage-page">
      <div className="hero-banner d-flex flex-column flex-lg-row align-items-center justify-content-between">
        <div className="hero-banner-content">
          <span className="hero-eyebrow">Trucky Hub</span>
          <h1 className="hero-title">
            {container.localeManager.strings.getTrucky}
          </h1>
          <div
            className="hero-subtitle"
            dangerouslySetInnerHTML={{
              __html: container.localeManager.strings.getTruckyInfo,
            }}
          ></div>
          <div className="hero-actions">
            <Button
              href="https://truckyapp.com/client/"
              target="_blank"
              tag="a"
              color="success"
              className="fw-bolder"
            >
              <FontAwesomeIcon icon="download"></FontAwesomeIcon>
              {container.localeManager.strings.download}
            </Button>
            {globalContext.member == null && (
              <Button
                color="primary"
                href={loginUrl}
                className="fw-bolder"
                outline
              >
                <FontAwesomeIcon icon={["fab", "steam"]}></FontAwesomeIcon>
                {container.localeManager.strings.continueWithSteam}
              </Button>
            )}
          </div>
          <div className="hero-platform-badges">
            <span className="hero-platform-badge">
              <FontAwesomeIcon icon={["fab", "windows"]} className="mx-0" />
              Windows
            </span>
            <span className="hero-platform-badge">
              <FontAwesomeIcon icon={["fab", "linux"]} className="mx-0" />
              Linux
            </span>
            <span className="hero-platform-badge">
              <FontAwesomeIcon icon={["fab", "apple"]} className="mx-0" />
              macOS
            </span>
          </div>
        </div>
        <div className="hero-visual d-none d-lg-flex">
          <FontAwesomeIcon icon="window-restore" className="hero-visual-icon mx-0"></FontAwesomeIcon>
        </div>
      </div>

      {statCards && (
        <div className="grid cols-4 hero-stats">
          {statCards.map((s) => (
            <Card key={s.key} className="stat-card">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fs-2 fw-bolder">{s.label}</span>
                  <span className={`icon-circle icon-circle-${s.color}`}>
                    <FontAwesomeIcon icon={s.icon} className="mx-0"></FontAwesomeIcon>
                  </span>
                </div>
                <div className="mt-10 fs-1 fw-bolder stat-card-value">
                  {s.value}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <NitroAdContainer
        container={container}
        id="hp-mid-2"
        adProps={{
          sizes: [
            ["728", "90"],
            ["970", "90"],
            ["970", "250"],
            ["300", "250"],
          ],
          mediaQuery:
            "(min-width: 1025px), (min-width: 768px) and (max-width: 1024px)",
        }}
      ></NitroAdContainer>
      <NitroAdContainer
        container={container}
        id="hp-mid-2-mobile"
        adProps={{
          sizes: [
            ["300", "250"],
            ["320", "100"],
            ["320", "50"],
          ],
          mediaQuery: "(min-width: 320px) and (max-width: 767px)",
        }}
      ></NitroAdContainer>
      <div className="companies-section align-items-center d-flex flex-column align-content-center justify-content-center">
        {companyStats != null && (
          <div className="fs-2 mb-5 text-center fw-bolder w-100">
            {container.localeManager.formatString(
              container.localeManager.strings.joinClaim,
              companyStats.count
            )}
            <div className="mt-5 grid cols-4">
              {companyStats.companies.map((m) => {
                return (
                  <Card
                    key={m.slug}
                    className="directory-company-card text-white"
                  >
                    <CardBody className="text-center d-flex flex-column">
                      <Link to={`/vtc/${m.slug}`}>
                        {getCompanyLogo(m)}
                        <CardTitle className="text-uppercase font-weight-bold">
                          <h4>{m.name}</h4>
                        </CardTitle>
                        <p className="mt-1 fst-italic company-slogan">
                          {m.slogan}
                        </p>
                        <div className="mt-auto company-badge-row">
                          {m.flag_url != null && (
                            <img src={m.flag_url} className="me-1" />
                          )}
                          {getCompanyStyleBadge(m)}
                        </div>
                      </Link>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
        <div className="d-flex flex-row gap-4 homepage-actions">
          {(globalContext.member == null ||
            (globalContext.member != null &&
              globalContext.member.company_id == null)) && (
            <Button color="success" onClick={() => navigate("/company/create")}>
              <FontAwesomeIcon icon="rocket"></FontAwesomeIcon>
              {container.localeManager.strings.registerYourCompany}
            </Button>
          )}
          {(globalContext.member == null ||
            (globalContext.member != null &&
              globalContext.member.company_id == null)) && (
            <Button color="primary" outline onClick={() => navigate("/directory")}>
              <FontAwesomeIcon icon="search"></FontAwesomeIcon>
              {container.localeManager.strings.searchACompany}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Homepage;
